/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDAvatar from "components/MDAvatar";
import Alert from "components/CustomAlert";

import noPreview from "assets/images/no-preview.png";
import { replaceHTMLEntityAnd } from "utils/helperFunctions";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const getProductName = (product) => {
  if (!product?.name) return "Untitled product";
  return replaceHTMLEntityAnd(product.name);
};

const getProductImage = (product) => {
  if (product?.images && product.images.length > 0 && product.images[0]?.src) {
    return product.images[0].src;
  }
  return noPreview;
};

function SelectProductsToSyncDialog({
  open,
  products = [],
  existingWooCommerceIds = [],
  onlyNew = false,
  onCancel,
  onConfirm,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const existingIdSet = useMemo(
    () => new Set((existingWooCommerceIds || []).map((id) => Number(id))),
    [existingWooCommerceIds]
  );

  const isExistingProduct = (product) => existingIdSet.has(Number(product.id));
  const isSelectable = (product) => !(onlyNew && isExistingProduct(product));

  useEffect(() => {
    if (open) {
      setSearch("");
      setStatusFilter("all");
      setSelectedIds(new Set());
    }
  }, [open, onlyNew]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (products || []).filter((product) => {
      const existing = existingIdSet.has(Number(product.id));
      if (statusFilter === "new" && existing) return false;
      if (statusFilter === "existing" && !existing) return false;

      if (!query) return true;

      const name = getProductName(product).toLowerCase();
      const sku = (product.sku || "").toLowerCase();
      return name.includes(query) || sku.includes(query);
    });
  }, [products, search, statusFilter, existingIdSet]);

  const selectableVisibleProducts = filteredProducts.filter(isSelectable);
  const selectedVisibleCount = selectableVisibleProducts.filter((product) =>
    selectedIds.has(Number(product.id))
  ).length;
  const allVisibleSelected =
    selectableVisibleProducts.length > 0 &&
    selectedVisibleCount === selectableVisibleProducts.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const selectedProducts = (products || []).filter((product) =>
    selectedIds.has(Number(product.id))
  );
  const addCount = selectedProducts.filter((product) => !isExistingProduct(product)).length;
  const updateCount = selectedProducts.filter((product) => isExistingProduct(product)).length;

  const toggleProduct = (product) => {
    if (!isSelectable(product)) return;

    const productId = Number(product.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const toggleVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableVisibleProducts.forEach((product) => {
        const productId = Number(product.id);
        if (checked) {
          next.add(productId);
        } else {
          next.delete(productId);
        }
      });
      return next;
    });
  };

  const handleConfirm = () => {
    if (selectedProducts.length === 0) return;
    onConfirm(selectedProducts);
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={onCancel}
      aria-labelledby="select-products-to-sync-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, pr: 6, color: "#3f3f3f" }} id="select-products-to-sync-title">
        Select products to sync
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <MDTypography variant="button" color="text" sx={{ display: "block", mb: 2 }}>
          {onlyNew
            ? "Choose which new products should be added. Existing products are listed for reference and will not be updated."
            : "Choose which products should be added or updated. Unselected products will be skipped."}
        </MDTypography>

        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <TextField
            size="small"
            placeholder="Search by name or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <MDButton
            size="small"
            color="info"
            variant={statusFilter === "all" ? "gradient" : "outlined"}
            onClick={() => setStatusFilter("all")}
          >
            All
          </MDButton>
          <MDButton
            size="small"
            color="success"
            variant={statusFilter === "new" ? "gradient" : "outlined"}
            onClick={() => setStatusFilter("new")}
          >
            New
          </MDButton>
          <MDButton
            size="small"
            color="warning"
            variant={statusFilter === "existing" ? "gradient" : "outlined"}
            onClick={() => setStatusFilter("existing")}
          >
            Existing
          </MDButton>
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="caption" color="text">
            {selectedIds.size} of {products.length} selected
            {selectedIds.size > 0 &&
              ` (${addCount} to add${onlyNew ? "" : `, ${updateCount} to update`})`}
          </MDTypography>
          <MDBox>
            <MDButton
              size="small"
              color="info"
              variant="text"
              onClick={() => toggleVisible(true)}
              disabled={selectableVisibleProducts.length === 0}
            >
              Select visible
            </MDButton>
            <MDButton
              size="small"
              color="secondary"
              variant="text"
              onClick={() => toggleVisible(false)}
              disabled={selectedVisibleCount === 0}
            >
              Clear visible
            </MDButton>
          </MDBox>
        </MDBox>

        {filteredProducts.length === 0 ? (
          <Alert severity="warning">No products match the current filters.</Alert>
        ) : (
          <Box
            sx={{
              maxHeight: "52vh",
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: "6px",
            }}
          >
            <MDBox
              display="flex"
              alignItems="center"
              px={1}
              py={0.5}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #eee",
              }}
            >
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onChange={(e) => toggleVisible(e.target.checked)}
                disabled={selectableVisibleProducts.length === 0}
              />
              <MDTypography variant="caption" fontWeight="bold">
                Select all visible ({selectableVisibleProducts.length})
              </MDTypography>
            </MDBox>
            <List
              dense
              disablePadding
              sx={{
                "& .MuiListItem-root": {
                  padding: 0,
                  borderBottom: "1px solid #f3f3f3",
                },
              }}
            >
              {filteredProducts.map((product) => {
                const productId = Number(product.id);
                const existing = isExistingProduct(product);
                const selectable = isSelectable(product);
                const checked = selectedIds.has(productId);
                const skuLabel = product.sku ? `SKU: ${product.sku}` : "No SKU";
                const typeLabel = product.type || "unknown";

                return (
                  <ListItem key={productId} disablePadding>
                    <ListItemButton
                      disabled={!selectable}
                      onClick={() => toggleProduct(product)}
                      sx={{
                        py: 1,
                        opacity: selectable ? 1 : 0.6,
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={!selectable}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemAvatar>
                        <MDAvatar
                          src={getProductImage(product)}
                          name={getProductName(product)}
                          size="sm"
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <MDTypography variant="button" fontWeight="medium">
                            {getProductName(product)}
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="caption" color="text">
                            {skuLabel} · {typeLabel}
                          </MDTypography>
                        }
                      />
                      {existing ? (
                        <MDBadge
                          badgeContent={onlyNew ? "Already synced" : "Update"}
                          color={onlyNew ? "secondary" : "warning"}
                          variant="gradient"
                          size="sm"
                        />
                      ) : (
                        <MDBadge badgeContent="New" color="success" variant="gradient" size="sm" />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onCancel} color="error" variant="text">
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          color="info"
          variant="gradient"
          disabled={selectedProducts.length === 0}
        >
          Sync selected ({selectedProducts.length})
        </MDButton>
      </DialogActions>
    </BootstrapDialog>
  );
}

export default SelectProductsToSyncDialog;
